import { combineReducers } from 'redux';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { DocumentInfo, DocumentListResponse } from 'generated/bffDocument/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import { decodeBase64, downloadFile, downloadUrl, encodedBufferToUTF8 } from 'helpers/downloadHandler';
import { mimeTypeToExtension } from 'helpers/mimeType';
import {
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createLoadingStateReducer,
  createReducer,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';

import { ExtendedDocumentsFilter, DocumentsFilterParams } from 'models/Document';
import { Pagination } from 'models/Pagination';

import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { selectContextInternalIsUserScreenFilterDisabled } from 'saga/context/internal/ducks';
import { selectProperty } from 'saga/property/ducks';

/* STATE */
export interface FilteredDocumentsState {
  data: DocumentInfo[];
  loadingStatus: LoadingStatus;
  totalCount: number;
  filter: ExtendedDocumentsFilter;
  pagination: Pagination;
}

/* ACTION TYPES */
export enum FilteredDocumentsActionTypes {
  SetFilter = '@@FilteredDocuments/SET_FILTER',
  SetLoading = '@@FilteredDocuments/SET_LOADING',
  SetPage = '@@FilteredDocuments/SET_PAGE',
  GetDocumentList = '@@FilteredDocuments/GET_DOCUMENT_LIST',
  DownloadDocuments = '@@FilteredDocuments/DOWNLOAD_DOCUMENT',
  ExportDocumentList = '@@FilteredDocuments/EXPORT_DOCUMENT_LIST',
  ClearPagination = '@@FilteredDocuments/CLEAR_PAGINATION',
}

/* ACTIONS */
export const setFilterAction = createActionCreator(FilteredDocumentsActionTypes.SetFilter);
export const setLoadingAction = createActionCreator(FilteredDocumentsActionTypes.SetLoading);
export const setPageAction = createActionCreator(FilteredDocumentsActionTypes.SetPage);
export const getDocumentListActions = createApiActionCreators(FilteredDocumentsActionTypes.GetDocumentList);
export const downloadDocumentsActions = createApiActionCreators(FilteredDocumentsActionTypes.DownloadDocuments);
export const exportDocumentListActions = createApiActionCreators(FilteredDocumentsActionTypes.ExportDocumentList);
export const clearPaginationAction = createActionCreator(FilteredDocumentsActionTypes.ClearPagination);

/* REDUCERS */
const initialState: FilteredDocumentsState = {
  data: [],
  loadingStatus: LoadingStatus.loading,
  totalCount: 0,
  filter: {},
  pagination: {
    page: 1,
    pageSize: 0,
    forceCheck: false,
    initPages: 1,
  },
};

const data = createReducer(initialState.data, {
  [FilteredDocumentsActionTypes.GetDocumentList]: {
    [RequestActionTypes.SUCCESS]: (state: DocumentInfo[], payload: DocumentListResponse & Pagination) => {
      if (payload.page > 1) {
        return [...state, ...(payload.documentList || [])];
      }

      return payload.documentList || [];
    },
    [RequestActionTypes.FAILURE]: () => [],
  },
});

const loadingStatus = createLoadingStateReducer(
  initialState.loadingStatus,
  {
    [FilteredDocumentsActionTypes.GetDocumentList]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
  },
  {
    [FilteredDocumentsActionTypes.GetDocumentList]: (state: LoadingStatus, status: LoadingStatus) => status,
  }
);

const totalCount = createReducer(initialState.totalCount, {
  [FilteredDocumentsActionTypes.GetDocumentList]: {
    [RequestActionTypes.SUCCESS]: (_state: number, payload: DocumentListResponse & Pagination) =>
      (payload.pageCount || 0) * payload.pageSize,
    [RequestActionTypes.FAILURE]: () => 0,
  },
});

const pagination = createReducer(initialState.pagination, {
  [FilteredDocumentsActionTypes.ClearPagination]: () => initialState.pagination,
  [FilteredDocumentsActionTypes.SetPage]: (_state: Pagination, payload: Pagination) => payload,
  [FilteredDocumentsActionTypes.SetFilter]: (state: Pagination) => ({
    ...state,
    page: 1,
  }),
  [FilteredDocumentsActionTypes.GetDocumentList]: {
    [RequestActionTypes.SUCCESS]: (state: Pagination) => ({
      ...state,
      page: state.page < state.initPages ? state.initPages : state.page,
    }),
  },
});

const filter = createReducer(initialState.filter, {
  [FilteredDocumentsActionTypes.SetFilter]: (
    state: ExtendedDocumentsFilter,
    payload: Partial<ExtendedDocumentsFilter>
  ) => payload,
});

export default combineReducers<FilteredDocumentsState>({
  data,
  loadingStatus,
  totalCount,
  filter,
  pagination,
});

/* SELECTORS*/
const selectFilteredDocuments = (state: AppState) => state.filteredDocuments;

export const selectFilteredDocumentsFilter = (state: AppState): DocumentsFilterParams =>
  selectFilteredDocuments(state).filter;

export const selectFilteredDocumentsLoading = (state: AppState): LoadingStatus =>
  selectFilteredDocuments(state).loadingStatus;

export const selectFilteredDocumentsPagination = (state: AppState): Pagination =>
  selectFilteredDocuments(state).pagination;

export const selectFilteredDocumentsData = (state: AppState): DocumentInfo[] => {
  const isInternal = selectContextInternalIsUserScreenFilterDisabled(state);

  return selectFilteredDocuments(state).data.filter((item) => item.extVisible || isInternal);
};

export const selectFilteredDocumentsTotalCount = (state: AppState): number => selectFilteredDocuments(state).totalCount;

/* SAGAS */
function* getDocumentDefaultPagination() {
  const pageSize = Number(selectProperty('gui.documentList.pageSize'));
  const initPages = Number(selectProperty('gui.documentList.loadPagesCount.Init'));

  const pagination: Pagination = yield select(selectFilteredDocumentsPagination);

  yield put(
    setPageAction({
      ...pagination,
      initPages,
      pageSize: pageSize || 50,
    })
  );
}

function* getDocumentList() {
  yield put(setLoadingAction(LoadingStatus.loading));

  const filter: ExtendedDocumentsFilter = yield select(selectFilteredDocumentsFilter);
  const pagination: Pagination = yield select(selectFilteredDocumentsPagination);

  if (!pagination.pageSize) {
    yield call(getDocumentDefaultPagination);

    return;
  }

  const paginationParam = {
    ...pagination,
    page: pagination.page < pagination.initPages ? 1 : pagination.page,
    pageSize: pagination.page < pagination.initPages ? pagination.pageSize * pagination.initPages : pagination.pageSize,
  };

  const request: ApiClientRequest = {
    apiCall: call(api.getDocumentList, yield getApiUrl(environment.urlConfig.documents), filter, paginationParam),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(
      getDocumentListActions.success({
        ...resp.data,
        ...paginationParam,
      })
    );
  } else {
    yield put(getDocumentListActions.failure());
  }
}

function* exportDocumentList({ payload: fileType }: any) {
  const filter: ExtendedDocumentsFilter = yield select(selectFilteredDocumentsFilter);

  const request: ApiClientRequest = {
    apiCall: call(api.getDocumentListToFile, yield getApiUrl(environment.urlConfig.documents), filter, fileType),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    const { fileContent, mimeType } = resp.data;
    const decoded =
      mimeType === 'text/csv'
        ? encodedBufferToUTF8(decodeBase64(fileContent.toString()), 'windows-1250', true)
        : decodeBase64(fileContent.toString());

    downloadFile(decoded, `vyhledane_dokumenty.${mimeTypeToExtension(mimeType)}`, mimeType);
  } else {
    yield put(exportDocumentListActions.failure());
  }
}

function* downloadDocument({ payload }: any) {
  const { documentGUID, objectStoreName } = payload;
  const request: ApiClientRequest = {
    apiCall: call(
      api.downloadDocument,
      yield getApiUrl(environment.urlConfig.documents),
      documentGUID,
      objectStoreName
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    const doc = resp.data.documentList && resp.data.documentList[0];

    downloadUrl(doc?.downloadUrl);
  } else {
    yield put(downloadDocumentsActions.failure());
  }
}

/* EXPORT */
export function* filteredDocumentsSaga() {
  yield takeLatest(
    [
      createActionType(FilteredDocumentsActionTypes.GetDocumentList, RequestActionTypes.REQUEST),
      FilteredDocumentsActionTypes.SetPage,
    ],
    getDocumentList
  );

  yield takeLatest(
    createActionType(FilteredDocumentsActionTypes.DownloadDocuments, RequestActionTypes.REQUEST),
    downloadDocument
  );

  yield takeLatest(
    createActionType(FilteredDocumentsActionTypes.ExportDocumentList, RequestActionTypes.REQUEST),
    exportDocumentList
  );
}
